import React from 'react'
import { useUser } from '@features/auth/hooks'
import { keyframes, useMediaQuery } from '@mui/material'
import dayjs from 'dayjs'
import {
  useRevisionListFeedbackRequestsQuery,
  useRevisionListRecordingsQuery,
  useRevisionUpdateRecordingMutation,
} from '@app/services/auto-generated/revision'
import { revisionApi } from '@app/services/revision'
import {
  Alert,
  Box,
  Button,
  CardActionArea,
  Divider,
  Grid,
  InputAdornment,
  inputBaseClasses,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@common/components'
import { FEEDBACK_ROUTE, SUMMARY_ROUTE } from '@common/constants/routes'
import { useAppDispatch } from '@common/hooks'
import {
  CheckCircleOutlineIcon,
  CheckIcon,
  KeyboardArrowDownIcon,
  LoopIcon,
  SearchIcon,
} from '@common/icons'
import errorHandler from '@common/utils/error-handler'
import { formatSeconds } from '@common/utils/time'
import { useLocation, useNavigate, useParams } from '@lib/router'
import { isUNSW } from '@utils'
import { CardWrapper } from './display-session.styles'

const DisplaySessions = (props: any) => {
  const [limit, setLimit] = React.useState(10)
  const {
    data: completedSessions,
    isLoading,
    isError,
    error,
    isSuccess,
    isFetching,
  } = useRevisionListRecordingsQuery({ limit: limit }, { refetchOnMountOrArgChange: true })

  const {
    data: ListRequest,
    isSuccess: isSuccessListRequest,
    isLoading: isLoadingListRequest,
    isError: isErrorListRequest,
    error: errorListRequest,
  } = useRevisionListFeedbackRequestsQuery()
  const { isTeacher } = useUser()
  const [searchTerm, setSearchTerm] = React.useState('')

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value)
  }

  const filteredRequest = ListRequest?.received?.filter((request) => {
    return request?.student_display_name?.toLowerCase().includes(searchTerm.toLowerCase())
  })

  const handleLimit = () => {
    setLimit(limit + 10)
  }
  const [updateReviwewFeedback] = useRevisionUpdateRecordingMutation()

  const getStatusRequest = (status: any) => {
    switch (status) {
      case 'accepted':
        return 'To Review'
      case 'reviewed':
        return 'Reviewed'
      case 'denied':
        return 'Denied'
      default:
        return 'To Review'
    }
  }
  const dispatch = useAppDispatch()
  const handleReview = async (lessonId: number, review: boolean) => {
    try {
      dispatch(
        revisionApi.util.updateQueryData('revisionListFeedbackRequests', undefined, (draft) => {
          if (!draft?.received) return draft
          const updatedReceived = draft.received.map((request) => {
            if (request.recording_id === lessonId) {
              return { ...request, status: review ? 'accepted' : 'reviewed' }
            }
            return request
          })
          draft.received = updatedReceived
          return draft
        }),
      )
      await updateReviwewFeedback({
        id: lessonId,
        updateRecordingRequest: { reviewed: !review },
      })
    } catch (error) {
      console.error(error)
    }
  }

  const [dateDesc, setDateDesc] = React.useState(true)
  const [dateDescRequest, setDateDescRequest] = React.useState(true)

  return (
    <Stack sx={{ height: '100%', width: '100%' }}>
      {/* <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: '600' }}>Upcoming</Typography>
        {isLoadingUpcomingSessions ? (
          <Skeleton variant='rectangular' height={80} sx={{ mt: 2 }} />
        ) : isErrorUpcomingSessions ? (
          <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
            {errorHandler(errorUpcomingSessions)}
          </Alert>
        ) : isSuccessUpcomingSessions ? (
          upcomingSessions?.length === 0 ? (
            <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              <Typography variant='body1'>You have no upcoming sessions at the moment</Typography>
            </Alert>
          ) : (
            <>
              {upcomingSessions?.map((session, i) => {
                return (
                  <SessionDisplayCard
                    key={i}
                    teacher={session.id}
                    session_package={session.id}
                    start_time={session.start_time}
                  />
                )
              })}
            </>
          )
        ) : null}
      </Box> */}
      {isTeacher && (
        <Box sx={{ mb: 2 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography sx={{ fontWeight: '600', marginTop: '10px' }}>
              Recordings to review
            </Typography>
            <Button color='neutral' onClick={() => setDateDescRequest(!dateDescRequest)}>
              <KeyboardArrowDownIcon
                sx={{
                  transform: dateDescRequest ? '' : 'rotate(180deg)',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
              date
            </Button>
          </Stack>
          {isLoadingListRequest ? (
            <Skeleton variant='rectangular' height={80} sx={{ mt: 2 }} />
          ) : isErrorListRequest ? (
            <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              {errorHandler(errorListRequest)}
            </Alert>
          ) : isSuccessListRequest ? (
            !ListRequest?.received ? (
              <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
                <Typography variant='body1'>
                  You have no upcoming sessions at the moment{' '}
                </Typography>
              </Alert>
            ) : (
              <>
                <TextField
                  id='search-request'
                  defaultValue={''}
                  fullWidth
                  color='tertiary'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder='Search by name ...'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    [`& .${inputBaseClasses.root}`]: {
                      marginTop: '15px',
                      borderRadius: '20px',
                    },
                  }}
                />
                {filteredRequest &&
                  [...filteredRequest]
                    ?.sort((a, b) => {
                      const dateA = dayjs(a.created_at)
                      const dateB = dayjs(b.created_at)
                      return dateDescRequest ? dateB.diff(dateA) : dateA.diff(dateB)
                    })
                    ?.map((session, i) => {
                      return (
                        <SessionDisplayCard
                          key={i}
                          feedbackId={session.recording_id}
                          userName={session.student_display_name || ''}
                          name='Recording'
                          reviewRequest={handleReview}
                          requestStatus={getStatusRequest(session.status)}
                          date={session.created_at}
                          onItemClick={props.onItemClick}
                        />
                      )
                    })}
                {filteredRequest?.length === 0 ? (
                  <Alert variant='outlined' severity='info' sx={{ mt: 2, mb: 1 }}>
                    No Feedback Requests Found
                  </Alert>
                ) : null}
              </>
            )
          ) : null}
        </Box>
      )}
      <Box id={'completed-recordings-bar'}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography sx={{ fontWeight: '600', marginTop: '10px' }}>
            Completed events and recordings
          </Typography>
          <Button color='neutral' onClick={() => setDateDesc(!dateDesc)}>
            <KeyboardArrowDownIcon
              sx={{
                transform: dateDesc ? '' : 'rotate(180deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
            date
          </Button>
        </Stack>
        {isLoading ? (
          <Skeleton variant='rectangular' height={80} sx={{ mt: 2 }} />
        ) : isError ? (
          <Alert severity='error' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
            {errorHandler(error)}
          </Alert>
        ) : isSuccess ? (
          completedSessions?.length === 0 ? (
            <Alert severity='info' variant='outlined' sx={{ mx: 'auto', mt: 2 }}>
              <Typography variant='body1'>You have no recordings at the moment </Typography>
            </Alert>
          ) : (
            <>
              {[...completedSessions]
                ?.sort((a, b) => {
                  const dateA = dayjs(a.created_at)
                  const dateB = dayjs(b.created_at)
                  return dateDesc ? dateB.diff(dateA) : dateA.diff(dateB)
                })
                ?.map((session, i) => {
                  return (
                    <SessionDisplayCard
                      key={session.id}
                      feedbackId={session.id}
                      status={session.processing_status || ''}
                      name='Pengguin AI'
                      tags={['English', formatSeconds(session?.duration)]}
                      date={session.created_at}
                      isRecent={i === 0}
                      onItemClick={props.onItemClick}
                    />
                  )
                })}
              {isFetching ? (
                <Skeleton variant='rectangular' height={80} sx={{ mt: 2, borderRadius: '20px' }} />
              ) : null}
              <Button
                variant='text'
                color='neutral'
                onClick={handleLimit}
                sx={{ display: 'flex', width: '120px', mx: 'auto', my: 3 }}
              >
                See more
              </Button>
            </>
          )
        ) : null}
      </Box>
    </Stack>
  )
}
export default DisplaySessions

interface SessionDisplayCardProps {
  name: string
  tags?: string[]
  date: string
  userName?: string
  feedbackId: number
  status?: string
  reviewRequest?: (lessonId: number, review: boolean) => void
  requestStatus?: string
  isRecent?: boolean
  onItemClick?: () => void
}

const SessionDisplayCard = (props: SessionDisplayCardProps) => {
  const {
    feedbackId,
    status,
    isRecent,
    tags,
    date,
    userName,
    reviewRequest,
    requestStatus,

    onItemClick = () => {},
  } = props
  const { feedback_id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const isMatcheMd = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const handleNavigate = () => {
    if (location.pathname.slice(0, SUMMARY_ROUTE.length) === SUMMARY_ROUTE) {
      navigate(`${SUMMARY_ROUTE}/${feedbackId}`, { preventScrollReset: true })
    } else {
      navigate(`${FEEDBACK_ROUTE}/${feedbackId}`, { preventScrollReset: true })
    }
    // Execute scroll after navigation
    setTimeout(() => {
      onItemClick()
    }, 1000)
  }
  const toolTipDate = dayjs.utc(date).local().format('HH:mm • MMM, D YYYY')
  const titleDate = isMatcheMd
    ? dayjs.utc(date).local().format('MMM DD')
    : dayjs.utc(date).local().format('MMMM DD')
  const formattedDate = dayjs.utc(date).local().format('h:mm A')

  const isActive =
    location.pathname === FEEDBACK_ROUTE || location.pathname === SUMMARY_ROUTE
      ? isRecent
      : feedback_id === feedbackId?.toString()
  return (
    <Tooltip title={toolTipDate} arrow disableHoverListener={!isMatcheMd}>
      <CardWrapper
        active={isActive}
        fullWidth
        sx={{
          cursor: 'pointer',
          outline: (theme) =>
            isUNSW()
              ? feedback_id === feedbackId?.toString()
                ? `solid 2px  ${theme.palette.secondary.main}`
                : `solid 0px  ${theme.palette.secondary.main}`
              : '',
        }}
      >
        <CardActionArea sx={{ p: 2 }} onClick={handleNavigate}>
          <Grid container spacing={1} sx={{ position: 'relative', width: '100%' }}>
            <Grid item xs={reviewRequest ? 8 : 10}>
              {userName ? (
                <Typography textAlign='start' sx={{ fontSize: '13px' }} noWrap>
                  {userName}
                </Typography>
              ) : null}
              {tags && (
                <Typography textAlign='start' sx={{ fontSize: '13px' }}>
                  {tags.join(' - ')}
                </Typography>
              )}
              <Stack
                direction='row'
                spacing={1}
                alignItems={'center'}
                justifyContent='flex-start'
                sx={{
                  borderRadius: '20px',
                  maxWidth: '180px',
                }}
                divider={<Divider orientation='vertical' flexItem />}
              >
                <Typography color='text.secondary' textAlign='center' sx={{ fontSize: '12px' }}>
                  {titleDate}
                </Typography>
                <Typography color='text.secondary' textAlign='center' sx={{ fontSize: '12px' }}>
                  {formattedDate}
                </Typography>
              </Stack>
            </Grid>
            {reviewRequest ? (
              <Grid item xs={4}>
                <Button
                  color='neutral'
                  variant='outlined'
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    reviewRequest(feedbackId, requestStatus === 'Reviewed')
                  }}
                  sx={{
                    minWidth: 'unset',
                    textTransform: 'initial',
                    borderRadius: '5px',
                    display: 'flex',
                    width: '120px',
                    py: 1,
                    // flexWrap: 'wrap',
                  }}
                >
                  {requestStatus === 'Reviewed' && <CheckIcon fontSize='small' />}
                  {/* <Checkbox
                    color='neutral'
                    checked={requestStatus === 'Reviewed'}
                    size='small'
                    sx={{ padding: 0 }}
                  /> */}
                  <Typography noWrap sx={{ fontSize: '12px', width: '62px' }}>
                    {requestStatus}
                  </Typography>
                </Button>
              </Grid>
            ) : (
              <Tooltip title={status || ''}>
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                  }}
                >
                  {status === 'PROCESSED' ? (
                    <CheckCircleOutlineIcon fontSize='small' color='success' />
                  ) : status === 'PROCESSING' ? (
                    <LoopIcon
                      color='info'
                      fontSize='small'
                      sx={{ transform: 'rotate(90deg)', animation: `${spin} 1s infinite linear` }}
                    />
                  ) : null}
                </Box>
              </Tooltip>
            )}
          </Grid>
        </CardActionArea>
      </CardWrapper>
    </Tooltip>
  )
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
`
